import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { AppDownloadModal } from "@/components/AppDownloadModal";
import { APPS_PUBLISHED } from "@/lib/appStore";
import { PasswordInput } from "@/components/PasswordInput";
import { PasswordStrength, isPasswordStrong } from "@/components/PasswordStrength";
import api from "@/lib/api";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const refCode = (sp.get("ref") || "").trim();
  const promoCode = (sp.get("promo") || "").trim().toUpperCase();
  const roleParam = ["collector", "company"].includes(sp.get("role")) ? sp.get("role") : "";
  const [form, setForm] = useState({ username: "", name: "", email: (sp.get("email") || "").trim(), phone: "", password: "", role: roleParam || (promoCode ? "collector" : "customer"), notify_consent: true, agree_fee: false, agree_terms: false, agree_contractor: false, ref: refCode, promo: promoCode });
  const [promoInfo, setPromoInfo] = useState(null);

  useEffect(() => {
    if (!promoCode) return;
    api.get(`/promo/validate`, { params: { code: promoCode } })
      .then(({ data }) => setPromoInfo(data))
      .catch(() => setPromoInfo({ valid: false }));
  }, [promoCode]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [appModal, setAppModal] = useState(false);
  const [pendingRole, setPendingRole] = useState("customer");
  const [pendingTrial, setPendingTrial] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const goNext = () => {
    setAppModal(false);
    if (pendingRole === "company") {
      navigate("/dashboard/company?tab=profile");
    } else if (pendingRole === "collector") {
      navigate(pendingTrial ? "/dashboard/collector" : "/subscribe");
    } else {
      navigate("/search");
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    if (!form.email.trim() && !form.phone.trim()) {
      setErr("Please provide an email address or phone number (or both).");
      setBusy(false);
      return;
    }
    const isPro = form.role === "collector" || form.role === "company";
    if (isPro && !form.agree_fee) {
      setErr(form.role === "company" ? "Companies must agree to the $10.99/month per-collector subscription to continue." : "Collectors must agree to the recurring $10.99/month subscription to continue.");
      setBusy(false);
      return;
    }
    if (isPro && !form.agree_contractor) {
      setErr(form.role === "company" ? "Please read and agree to the Company Account Agreement to continue." : "Please read and agree to the Independent Contractor Agreement to continue.");
      setBusy(false);
      return;
    }
    if (form.role === "customer" && !form.agree_terms) {
      setErr("Please agree to the Customer Acknowledgment and Release to continue.");
      setBusy(false);
      return;
    }
    if (!isPasswordStrong(form.password)) {
      setErr("Please choose a stronger password: at least 8 characters with an uppercase letter, a lowercase letter, and a number.");
      setBusy(false);
      return;
    }
    try {
      const u = await register({ ...form, agree_company: form.role === "company" && form.agree_contractor, ein: "" });
      setPendingRole(u.role);
      setPendingTrial(!!u.trial_applied);
      if (APPS_PUBLISHED) {
        setAppModal(true);
      } else {
        if (u.role === "company") {
          navigate("/dashboard/company?tab=profile");
        } else if (u.role === "collector") {
          navigate(u.trial_applied ? "/dashboard/collector" : "/subscribe");
        } else {
          navigate("/search");
        }
      }
    } catch (e) {
      setErr(e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="container-x py-20 max-w-md" data-testid="register-page">
      <AppDownloadModal open={appModal} onOpenChange={(o) => { if (!o) goNext(); }} onContinue={goNext} role={pendingRole} />
      <h1 className="font-heading text-3xl text-[#1E3F33] font-semibold">Create your account</h1>
      <p className="text-[#5C6B64] mt-2">Collectors must sign up. Customers don't have to — but if you do, we'll save your contact info so you don't have to retype it each time.</p>

      {refCode && (
        <div data-testid="referral-banner" className="mt-4 rounded-xl border border-[#2D6A4F]/30 bg-[#2D6A4F]/5 px-4 py-3 text-sm text-[#1E3F33]">
          🎉 You were invited by a friend — welcome! Complete sign-up and you'll be credited to them.
        </div>
      )}

      {promoCode && promoInfo?.valid && (
        <div data-testid="promo-banner" className="mt-4 rounded-xl border border-[#CC5A3A]/40 bg-[#CC5A3A]/8 px-4 py-3 text-sm text-[#1E3F33]">
          🎁 <strong>{promoInfo.trial_days}-day free trial</strong> unlocked! Sign up as a collector below and your subscription is free for {promoInfo.trial_days} days — no card required.
        </div>
      )}
      {promoCode && promoInfo && !promoInfo.valid && (
        <div data-testid="promo-banner-invalid" className="mt-4 rounded-xl border border-[#8A9A86]/30 bg-[#8A9A86]/10 px-4 py-3 text-sm text-[#5C6B64]">
          This promo link isn’t valid{promoInfo.reason ? ` (${promoInfo.reason})` : ""}. You can still sign up normally.
        </div>
      )}

      <div className="mt-8 grid grid-cols-3 gap-2" data-testid="role-toggle">
        {[
          { v: "customer", l: "I'm a customer" },
          { v: "collector", l: "I'm a collector" },
          { v: "company", l: "We're a company" },
        ].map((r) => (
          <button
            key={r.v}
            type="button"
            data-testid={`role-${r.v}-button`}
            onClick={()=>setForm({...form, role:r.v})}
            className={`rounded-xl border px-2 py-3 text-xs sm:text-sm font-medium transition-colors ${
              form.role === r.v
                ? "bg-[#1E3F33] text-white border-[#1E3F33]"
                : "bg-white text-[#1E3F33] border-[#1E3F33]/15 hover:border-[#1E3F33]/40"
            }`}
          >
            {r.l}
          </button>
        ))}
      </div>

      <form onSubmit={onSubmit} className="mt-8 space-y-5">
        <div>
          <label className="label-soft">Username</label>
          <input
            data-testid="register-username-input"
            required
            minLength={3}
            maxLength={30}
            className="input-soft"
            value={form.username}
            onChange={set("username")}
            placeholder="Choose a username"
            autoCapitalize="none"
            autoComplete="username"
          />
          <p className="text-xs text-[#8A9A86] mt-1.5">3–30 characters. Any characters allowed.</p>
        </div>
        <div>
          <label className="label-soft">{form.role === "customer" ? "Full name" : "Business name"}</label>
          <input data-testid="register-name-input" required className="input-soft" value={form.name} onChange={set("name")} />
        </div>
        <div>
          <label className="label-soft">Email <span className="text-[#8A9A86] font-normal">(email or phone required)</span></label>
          <input data-testid="register-email-input" type="email" className="input-soft" value={form.email} onChange={set("email")} />
        </div>
        <div>
          <label className="label-soft">Phone <span className="text-[#8A9A86] font-normal">(email or phone required)</span></label>
          <input data-testid="register-phone-input" type="tel" className="input-soft" value={form.phone} onChange={set("phone")} placeholder="e.g., +1 555 555 5555"/>
        </div>

        <div className="rounded-xl border border-[#CC5A3A]/30 bg-[#CC5A3A]/5 p-4" data-testid="contact-emphasis">
          <p className="text-sm text-[#1E3F33] font-medium">How we'll reach you</p>
          <p className="text-xs text-[#5C6B64] mt-1">
            The email and phone number you provide are exactly how we'll contact you — we send updates, collector responses, and your request tracking link by <span className="font-medium text-[#1E3F33]">email</span> and <span className="font-medium text-[#1E3F33]">text message (SMS)</span>. Standard message &amp; data rates may apply.
          </p>
          <label className="mt-3 flex items-start gap-2.5 cursor-pointer" data-testid="notify-consent-row">
            <input
              type="checkbox"
              data-testid="notify-consent-checkbox"
              checked={form.notify_consent}
              onChange={(e)=>setForm({...form, notify_consent: e.target.checked})}
              className="mt-0.5 w-4 h-4 accent-[#2D6A4F]"
            />
            <span className="text-xs text-[#1E3F33]">Yes, send me email and SMS text notifications about my requests and collector updates.</span>
          </label>
        </div>
        {(form.role === "collector" || form.role === "company") && (
          <div className="rounded-xl border border-[#2D6A4F]/30 bg-[#2D6A4F]/5 p-4" data-testid="fee-agreement">
            <p className="text-sm text-[#1E3F33] font-medium">Collector subscription — $10.99/month{form.role === "company" ? " per collector" : ""}</p>
            <p className="text-xs text-[#5C6B64] mt-1">
              {form.role === "company"
                ? <>To receive and get connected to customers, each collector listed under your company needs an active <span className="font-medium text-[#1E3F33]">recurring $10.99/month subscription</span>. Your total adjusts as you add or remove collectors. Add your company details and collectors right after sign-up. Cancel anytime.</>
                : <>To receive and get connected to customers, collectors must maintain an active <span className="font-medium text-[#1E3F33]">recurring $10.99/month subscription</span>. You'll be taken to secure checkout right after sign-up. Cancel anytime.</>}
            </p>
            <label className="mt-3 flex items-start gap-2.5 cursor-pointer" data-testid="fee-agreement-row">
              <input
                type="checkbox"
                data-testid="fee-agreement-checkbox"
                checked={form.agree_fee}
                onChange={(e)=>setForm({...form, agree_fee: e.target.checked})}
                className="mt-0.5 w-4 h-4 accent-[#2D6A4F]"
              />
              <span className="text-xs text-[#1E3F33]">I agree to the recurring $10.99/month subscription{form.role === "company" ? " for each collector listed under my company" : ""} required to receive customers.</span>
            </label>
          </div>
        )}
        {(form.role === "collector" || form.role === "company") && (
          <label className="flex items-start gap-2.5 cursor-pointer" data-testid="contractor-terms-row">
            <input
              type="checkbox"
              data-testid="contractor-terms-checkbox"
              checked={form.agree_contractor}
              onChange={(e)=>setForm({...form, agree_contractor: e.target.checked})}
              className="mt-0.5 w-4 h-4 accent-[#2D6A4F]"
            />
            {form.role === "company"
              ? <span className="text-xs text-[#1E3F33]">I have read and agree to the <a href="/legal/company-agreement" target="_blank" rel="noreferrer" className="text-[#CC5A3A] font-medium hover:underline" data-testid="contractor-terms-link">Company Account Agreement</a> on behalf of my company and each collector registered under it.</span>
              : <span className="text-xs text-[#1E3F33]">I have read and agree to the <a href="/legal/collector-agreement" target="_blank" rel="noreferrer" className="text-[#CC5A3A] font-medium hover:underline" data-testid="contractor-terms-link">Independent Contractor Agreement</a>.</span>}
          </label>
        )}
        {form.role === "customer" && (
          <label className="flex items-start gap-2.5 cursor-pointer" data-testid="customer-terms-row">
            <input
              type="checkbox"
              data-testid="customer-terms-checkbox"
              checked={form.agree_terms}
              onChange={(e)=>setForm({...form, agree_terms: e.target.checked})}
              className="mt-0.5 w-4 h-4 accent-[#2D6A4F]"
            />
            <span className="text-xs text-[#1E3F33]">I have read and agree to the <a href="/legal/customer-acknowledgment" target="_blank" rel="noreferrer" className="text-[#CC5A3A] font-medium hover:underline" data-testid="customer-terms-link">Customer Acknowledgment and Release</a>.</span>
          </label>
        )}
        <div>
          <label className="label-soft">Password</label>
          <PasswordInput testId="register-password-input" toggleTestId="register-password-toggle" required minLength={8} value={form.password} onChange={set("password")} autoComplete="new-password" />
          <PasswordStrength password={form.password} testId="register-password-strength" />
        </div>
        {err && <div data-testid="register-error" className="text-sm text-[#E63946]">{err}</div>}
        <button data-testid="register-submit-button" disabled={busy} className="btn-primary w-full">
          {busy ? "Creating account…" : "Create account"}
        </button>
      </form>

      <p className="text-sm text-[#5C6B64] mt-6">
        Already have an account? <Link to="/login" data-testid="register-to-login" className="text-[#CC5A3A] font-medium">Sign in</Link>
      </p>
    </div>
  );
}
